import React from 'react';
import { createLibrary } from '../data/routeLibrary';

const LibraryPanel = ({ onHoverRoute }) => {
    const library = React.useMemo(() => createLibrary(), []);
    const offensePack = library.getPack('core_offense');
    const defensePack = library.getPack('core_defense');

    const groupedRoutes = React.useMemo(() => {
        const groups = {};
        offensePack?.routes.forEach(r => {
            if (!groups[r.category]) groups[r.category] = [];
            groups[r.category].push(r);
        });
        defensePack?.routes.forEach(r => {
            const catName = `Defense: ${r.category}`;
            if (!groups[catName]) groups[catName] = [];
            groups[catName].push(r);
        });
        return groups;
    }, [offensePack, defensePack]);

    const [searchTerm, setSearchTerm] = React.useState('');
    const [expandedGroups, setExpandedGroups] = React.useState({});

    React.useEffect(() => {
        if (Object.keys(groupedRoutes).length > 0) {
            const init = {};
            Object.keys(groupedRoutes).forEach(cat => { init[cat] = true; });
            setExpandedGroups(init);
        }
    }, [groupedRoutes]);

    const toggleGroup = (g) => setExpandedGroups(prev => ({ ...prev, [g]: !prev[g] }));

    const filteredGroups = React.useMemo(() => {
        if (!searchTerm) return groupedRoutes;
        const out = {};
        Object.entries(groupedRoutes).forEach(([cat, routes]) => {
            const matches = routes.filter(r =>
                r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                r.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                r.tags?.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            if (matches.length > 0) out[cat] = matches;
        });
        return out;
    }, [groupedRoutes, searchTerm]);

    const handleDragStart = (e, route) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ type: 'route-preset', routeId: route.id, packId: route.id.startsWith('blitz') || route.id.startsWith('deep_half') ? 'core_defense' : 'core_offense' }));
        e.dataTransfer.effectAllowed = 'copy';
    };

    return (
        <div className="flex flex-col h-full min-h-0 overflow-hidden">
            <div className="px-1 mb-3">
                <div className="relative group">
                    <input
                        type="text"
                        placeholder="Search concepts..."
                        className="w-full bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-lg px-9 py-2 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all shadow-inner"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 group-focus-within:text-blue-500 transition-colors text-sm">🔍</span>
                    {searchTerm && (
                        <button
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                            onClick={() => setSearchTerm('')}
                        >×</button>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 space-y-4 custom-scrollbar">
                {Object.entries(filteredGroups).map(([category, routes]) => (
                    <div key={category} className="group/cat">
                        <button
                            className="w-full flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-gray-800/40 transition-colors group"
                            onClick={() => toggleGroup(category)}
                        >
                            <div className="flex items-center gap-2">
                                <div className={`w-1 h-3 rounded-full ${category.startsWith('Defense') ? 'bg-red-500/50' : 'bg-blue-500/50'}`} />
                                <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">{category}</span>
                            </div>
                            <span className="text-[10px] text-gray-600 group-hover:text-gray-400 transition-transform duration-200" style={{ transform: expandedGroups[category] ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                        </button>

                        {(expandedGroups[category] || searchTerm) && (
                            <div className="grid grid-cols-2 gap-2 p-1 mt-1 animate-in fade-in slide-in-from-top-1 duration-200">
                                {routes.map(route => (
                                    <div
                                        key={route.id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, route)}
                                        onMouseEnter={() => onHoverRoute?.(route)}
                                        className="relative bg-gray-900/40 hover:bg-gray-800/80 border border-gray-800 hover:border-blue-500/30 rounded-lg p-2.5 cursor-grab active:cursor-grabbing group/item transition-all hover:shadow-lg hover:shadow-blue-500/5"
                                        title={route.name}
                                    >
                                        <div className="flex flex-col gap-1">
                                            <div className="text-[11px] text-gray-300 font-bold leading-tight group-hover/item:text-white transition-colors">{route.name}</div>
                                            <div className="flex gap-1 overflow-hidden">
                                                {route.tags?.slice(0, 1).map(t => (
                                                    <span key={t} className="text-[8px] text-gray-600 bg-black/20 px-1.5 py-0.5 rounded-full lowercase tracking-tighter group-hover/item:text-blue-400/70 transition-colors">
                                                        #{t}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="absolute top-1 right-1 opacity-0 group-hover/item:opacity-100 transition-opacity">
                                            <span className="text-[10px] text-blue-500/50">⠿</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const RosterPanel = ({ roster }) => {
    const handleDragStart = (e, player) => {
        e.dataTransfer.setData('application/json', JSON.stringify({
            type: 'roster-player',
            playerData: {
                name: player.name,
                number: player.number,
                color: player.iconColor
            }
        }));
        e.dataTransfer.effectAllowed = 'copy';
    };

    return (
        <div className="flex flex-col h-full min-h-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto pr-1 space-y-2 custom-scrollbar p-1">
                {[...roster].sort((a, b) => (b.overallRank || 0) - (a.overallRank || 0)).map(p => (
                    <div
                        key={p.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, p)}
                        className="flex items-center gap-3 p-2 bg-gray-900/40 border border-gray-800 hover:border-blue-500/30 rounded-lg cursor-grab active:cursor-grabbing transition-all hover:bg-gray-800/80 group/roster"
                    >
                        <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black shadow-sm"
                            style={{ backgroundColor: p.iconColor || '#3b82f6', color: ['#ffffff', '#fbbf24'].includes(p.iconColor) ? 'black' : 'white' }}
                        >
                            {p.number || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-bold text-gray-300 truncate group-hover/roster:text-white transition-colors">{p.name || 'Unnamed'}</div>
                            <div className="text-[8px] text-gray-500 font-black uppercase tracking-tighter">{p.position || 'N/A'}</div>
                        </div>
                        <div className="text-[10px] font-black text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded leading-none">
                            {p.overallRank || 5}
                        </div>
                    </div>
                ))}
                {roster.length === 0 && (
                    <div className="text-center py-12">
                        <div className="text-2xl mb-2 opacity-20">👤</div>
                        <div className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Roster is empty</div>
                        <div className="text-[8px] text-gray-600 mt-1 uppercase">Add players in Team Roster</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default function LeftRailBuilder({
    selectedTool, setSelectedTool,
    onHoverRoute,
    drawingSettings, setDrawingSettings,
    smoothingLevel, setSmoothingLevel,
    straightenLevel, setStraightenLevel,
    straightLineMode, setStraightLineMode,
    clearRoutes, onFlipPlay,
    // Context Props
    teamType, playbookName, playName,
    offenseFormation, defenseFormation,
    availableTypes, availablePlaybooks,
    availableOffenseFormations = [], availableDefenseFormations = [],
    onTypeChange, onPlaybookChange, onFormationChange, // Handlers
    onNewPlay, onSavePlay, onDuplicatePlay, canDuplicate = true,
    globalSettings, setGlobalSettings,
    roster = []
}) {
    return (
        <div className="qr-rail flex flex-col h-full">
            {/* Context & Actions Section */}
            <div className="mb-4 pb-4 border-b border-gray-700 flex-shrink-0">
                <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-2">Context</div>

                {/* Hierarchy Selectors - REORDERED: Playbook -> Type -> Formation */}
                <div className="flex flex-col gap-2 mb-3">
                    <select
                        className="qr-select text-xs w-full bg-gray-800 border border-gray-700 rounded px-1 py-1"
                        value={playbookName}
                        onChange={(e) => onPlaybookChange(e.target.value)}
                    >
                        {availablePlaybooks.map(pb => <option key={pb} value={pb}>{pb}</option>)}
                    </select>

                    <select
                        className="qr-select text-xs w-full bg-gray-800 border border-gray-700 rounded px-1 py-1"
                        value={teamType}
                        onChange={(e) => onTypeChange(e.target.value)}
                    >
                        {availableTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>

                    {/* Split Formations */}
                    <div className="flex flex-col gap-1 mt-1">
                        <label className="text-[9px] text-gray-500 uppercase">Offense</label>
                        <select
                            className="qr-select text-xs w-full bg-gray-800 border border-gray-700 rounded px-1 py-1"
                            value={offenseFormation || ''}
                            onChange={(e) => onFormationChange(e.target.value, 'offense')}
                        >
                            <option value="" disabled>Select Offense</option>
                            {availableOffenseFormations.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </div>

                    <div className="flex flex-col gap-1 mt-1">
                        <label className="text-[9px] text-gray-500 uppercase">Defense</label>
                        <select
                            className="qr-select text-xs w-full bg-gray-800 border border-gray-700 rounded px-1 py-1"
                            value={defenseFormation || ''}
                            onChange={(e) => onFormationChange(e.target.value, 'defense')}
                        >
                            <option value="">None</option>
                            {availableDefenseFormations.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </div>
                </div>

                <div className="text-sm text-white font-bold mb-3 truncate px-1" title={playName}>
                    {playName}
                </div>

                <div className="grid grid-cols-3 gap-1">
                    <button
                        className="flex flex-col items-center justify-center bg-gray-800 hover:bg-gray-700 p-2 rounded border border-gray-700 text-xs text-gray-300"
                        onClick={onNewPlay}
                        title="Create New Play"
                    >
                        <span className="text-lg mb-1">📄</span>
                        New
                    </button>
                    <button
                        className={`flex flex-col items-center justify-center p-2 rounded border text-xs transition-colors ${canDuplicate ? 'bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-300' : 'bg-gray-900 border-gray-800 text-gray-600 cursor-not-allowed opacity-50'}`}
                        onClick={onDuplicatePlay}
                        disabled={!canDuplicate}
                        title={canDuplicate ? "Duplicate Current Play" : "Duplication disabled by owner"}
                    >
                        <span className="text-lg mb-1">📑</span>
                        Copy
                    </button>
                    <button
                        className="flex flex-col items-center justify-center bg-blue-900/40 hover:bg-blue-900/60 p-2 rounded border border-blue-800 text-xs text-blue-200"
                        onClick={onSavePlay}
                        title="Save Play"
                    >
                        <span className="text-lg mb-1">💾</span>
                        Save
                    </button>
                </div>
            </div>

            {/* Tools & Library Tabs */}
            <div className="flex bg-gray-900 rounded-lg p-1 mb-3 mx-1 gap-1 border border-gray-800">
                <button
                    title="Design Tools (Select, Draw, Add Players)"
                    className={`flex-1 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-md transition-all ${selectedTool !== 'library' && selectedTool !== 'roster' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'}`}
                    onClick={() => setSelectedTool('select')}
                >
                    Tools
                </button>
                <button
                    title="Quick Routes (Drag and Drop presets onto players)"
                    className={`flex-1 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-md transition-all ${selectedTool === 'library' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'}`}
                    onClick={() => setSelectedTool('library')}
                >
                    Routes
                </button>
                <button
                    title="Team Roster (Drag and Drop onto players)"
                    className={`flex-1 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-md transition-all ${selectedTool === 'roster' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'}`}
                    onClick={() => setSelectedTool('roster')}
                >
                    Roster
                </button>
            </div>

            {selectedTool === 'library' ? (
                <LibraryPanel onHoverRoute={onHoverRoute} />
            ) : selectedTool === 'roster' ? (
                <RosterPanel roster={roster} />
            ) : (
                <div className="qr-group flex-shrink-0">
                    <div className="qr-label">Active Tool</div>
                    <div className="flex flex-col gap-1.5">
                        <button
                            title="Select and Move Players (Shortcut: S)"
                            className={`qr-tool flex items-center gap-3 px-3 py-2 rounded-lg border font-bold text-xs transition-all ${selectedTool === 'select' ? 'active bg-blue-600/20 border-blue-500 text-blue-400 shadow-inner' : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'}`}
                            onClick={() => setSelectedTool('select')}
                        >
                            <span className="text-sm">🖱️</span> Select
                        </button>
                        <button
                            title="Freehand Draw Route (Shortcut: D)"
                            className={`qr-tool flex items-center gap-3 px-3 py-2 rounded-lg border font-bold text-xs transition-all ${selectedTool === 'draw' ? 'active bg-blue-600/20 border-blue-500 text-blue-400 shadow-inner' : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'}`}
                            onClick={() => setSelectedTool('draw')}
                        >
                            <span className="text-sm">✏️</span> Draw Route
                        </button>

                        {/* Precision Draw Assistant Controls (Visible when Draw is active) */}
                        {selectedTool === 'draw' && (
                            <div className="pl-2 pr-1 py-1.5 mt-1 mb-2 bg-blue-900/10 border border-blue-500/20 rounded-lg flex flex-col gap-1.5">
                                <div className="text-[9px] text-blue-400/80 font-black uppercase tracking-tighter">Precision Assist</div>
                                <label className="flex items-center gap-2 text-[10px] text-gray-300 cursor-pointer hover:text-white transition-colors" title="Lock your drawing to 45 degree increments">
                                    <input
                                        type="checkbox"
                                        checked={straightLineMode}
                                        onChange={(e) => setStraightLineMode(e.target.checked)}
                                        className="w-3 h-3 rounded accent-blue-500"
                                    />
                                    <span>Snap Straight Lines</span>
                                </label>
                            </div>
                        )}

                        <div className="h-px bg-gray-800 my-1 mx-2" />

                        <button
                            title="Add Offense Player to Field"
                            className={`qr-tool flex items-center gap-3 px-3 py-2 rounded-lg border font-bold text-xs transition-all ${selectedTool === 'offense' ? 'active bg-blue-600/20 border-blue-500 text-blue-400 shadow-inner' : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'}`}
                            onClick={() => setSelectedTool('offense')}
                        >
                            <span className="text-sm">🔵</span> Add Offense
                        </button>
                        <button
                            title="Add Defense Player to Field"
                            className={`qr-tool flex items-center gap-3 px-3 py-2 rounded-lg border font-bold text-xs transition-all ${selectedTool === 'defense' ? 'active bg-blue-600/20 border-blue-500 text-blue-400 shadow-inner' : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'}`}
                            onClick={() => setSelectedTool('defense')}
                        >
                            <span className="text-sm">🔴</span> Add Defense
                        </button>
                        <button
                            title="Add Football to Field"
                            className={`qr-tool flex items-center gap-3 px-3 py-2 rounded-lg border font-bold text-xs transition-all ${selectedTool === 'ball' ? 'active bg-blue-600/20 border-blue-500 text-blue-400 shadow-inner' : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'}`}
                            onClick={() => setSelectedTool('ball')}
                        >
                            <span className="text-sm">🏈</span> Add Ball
                        </button>
                    </div>
                </div>
            )}

            {/* Persistent Route Actions (Always Visible) */}
            <div className="qr-group mt-2 border-t border-gray-700 pt-2 bg-gray-900/50 flex-shrink-0">
                <div className="qr-label">Route Actions</div>
                <div className="flex flex-col gap-1 mb-2">
                    {/* Smoothing Slider */}
                    <div className="flex flex-col justify-center bg-gray-800 border border-gray-700 rounded px-2 py-1">
                        <div className="flex justify-between text-[8px] text-gray-400 mb-1">
                            <span>Smooth</span>
                            <span>{smoothingLevel}</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="10"
                            step="1"
                            value={smoothingLevel}
                            onChange={(e) => setSmoothingLevel(parseInt(e.target.value))}
                            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>

                    {/* Straighten Slider */}
                    <div className="flex flex-col justify-center bg-gray-800 border border-gray-700 rounded px-2 py-1">
                        <div className="flex justify-between text-[8px] text-gray-400 mb-1">
                            <span>Straighten</span>
                            <span>{straightenLevel}</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="10"
                            step="1"
                            value={straightenLevel}
                            onChange={(e) => setStraightenLevel(parseInt(e.target.value))}
                            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-2">
                        <button
                            className="text-[10px] text-gray-400 hover:text-white border border-gray-700 px-2 py-1.5 rounded bg-gray-800 flex items-center justify-center gap-1.5 transition-colors hover:bg-gray-700"
                            onClick={clearRoutes}
                        >
                            <span>🗑️</span> Clear
                        </button>
                        <button
                            className="text-[10px] text-blue-300 hover:text-white border border-blue-900/50 px-2 py-1.5 rounded bg-blue-900/20 flex items-center justify-center gap-1.5 transition-colors hover:bg-blue-900/40"
                            onClick={onFlipPlay}
                            title="Flip all players and routes horizontally"
                        >
                            <span>↔️</span> Flip
                        </button>
                    </div>

                    {/* Draw Straight Lines Toggle */}
                    <div className="mt-1">
                        <label className={`flex items-center gap-2 text-[10px] px-2 py-1 rounded border transition-colors cursor-pointer ${straightLineMode ? 'bg-blue-900/30 border-blue-700 text-blue-200' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200'}`}>
                            <input
                                type="checkbox"
                                checked={straightLineMode}
                                onChange={(e) => setStraightLineMode(e.target.checked)}
                                className="w-3 h-3 rounded cursor-pointer accent-blue-500"
                            />
                            <span>Draw Straight Lines</span>
                        </label>
                    </div>
                </div>

                {/* Style selection visible only when Drawing to save space/context */}
                {selectedTool === 'draw' && (
                    <div className="flex gap-2">
                        {['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#64748b', '#000000', '#ffffff'].map(c => (
                            <button
                                key={c}
                                title={c}
                                className={`w-4 h-4 rounded-full border ${drawingSettings.color === c ? 'border-white scale-110' : 'border-transparent'}`}
                                style={{ backgroundColor: c }}
                                onClick={() => setDrawingSettings(s => ({ ...s, color: c }))}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Global Style Section */}
            <div className="qr-group mt-2 flex-grow-0">
                <div className="qr-label">Global Style</div>

                <div className="flex flex-col gap-3">
                    {/* Offense Defaults */}
                    <div>
                        <div className="text-[9px] text-gray-500 uppercase mb-1">Offense Default</div>
                        <div className="flex items-center gap-2">
                            {['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#64748b', '#000000', '#ffffff'].map(c => (
                                <button
                                    key={c}
                                    title={c}
                                    className={`w-3 h-3 rounded-full border ${globalSettings?.offenseColor === c ? 'border-white scale-110' : 'border-gray-600'}`}
                                    style={{ backgroundColor: c }}
                                    onClick={() => setGlobalSettings(s => ({ ...s, offenseColor: c }))}
                                />
                            ))}
                            <div className="w-[1px] h-3 bg-gray-700 mx-1" />
                            {['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#64748b', '#000000', '#ffffff'].map(c => (
                                <button
                                    key={c}
                                    title={`Route: ${c}`}
                                    className={`w-3 h-3 rounded-full border ${globalSettings?.offenseRouteColor === c ? 'border-white scale-110' : 'border-gray-600'}`}
                                    style={{ backgroundColor: c }}
                                    onClick={() => setGlobalSettings(s => ({ ...s, offenseRouteColor: c }))}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Defense Defaults */}
                    <div>
                        <div className="text-[9px] text-gray-500 uppercase mb-1">Defense Default</div>
                        <div className="flex items-center gap-2">
                            {['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#64748b', '#000000', '#ffffff'].map(c => (
                                <button
                                    key={c}
                                    title={c}
                                    className={`w-3 h-3 rounded-full border ${globalSettings?.defenderColor === c ? 'border-white scale-110' : 'border-gray-600'}`}
                                    style={{ backgroundColor: c }}
                                    onClick={() => setGlobalSettings(s => ({ ...s, defenderColor: c }))}
                                />
                            ))}
                            <div className="w-[1px] h-3 bg-gray-700 mx-1" />
                            {['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#64748b', '#000000', '#ffffff'].map(c => (
                                <button
                                    key={c}
                                    title={`Route: ${c}`}
                                    className={`w-3 h-3 rounded-full border ${globalSettings?.defenderRouteColor === c ? 'border-white scale-110' : 'border-gray-600'}`}
                                    style={{ backgroundColor: c }}
                                    onClick={() => setGlobalSettings(s => ({ ...s, defenderRouteColor: c }))}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Toggles */}
                    <div className="flex flex-col gap-1 mt-1 border-t border-gray-800 pt-2">
                        <label className="flex items-center gap-2 text-[10px] text-gray-400 hover:text-gray-200 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!globalSettings?.autoSequentialColor}
                                onChange={(e) => setGlobalSettings(s => ({ ...s, autoSequentialColor: e.target.checked }))}
                                className="w-3 h-3 accent-blue-500"
                            />
                            <span>Different color per player</span>
                        </label>
                        <label className="flex items-center gap-2 text-[10px] text-gray-400 hover:text-gray-200 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!globalSettings?.showAsNumber}
                                onChange={(e) => setGlobalSettings(s => ({ ...s, showAsNumber: e.target.checked }))}
                                className="w-3 h-3 accent-blue-500"
                            />
                            <span>Show player as number</span>
                        </label>
                        <label className="flex items-center gap-2 text-[10px] text-gray-400 hover:text-gray-200 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!globalSettings?.showPlayerNames}
                                onChange={(e) => setGlobalSettings(s => ({ ...s, showPlayerNames: e.target.checked }))}
                                className="w-3 h-3 accent-blue-500"
                            />
                            <span>Show player names</span>
                        </label>
                        <div className="flex flex-col gap-1 border-t border-gray-700/50 pt-2 mt-1">
                            <div className="flex items-center justify-between group">
                                <label className="flex items-center gap-2 text-[10px] text-gray-400 hover:text-gray-200 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!globalSettings?.walkthroughMode}
                                        onChange={(e) => setGlobalSettings(s => ({ ...s, walkthroughMode: e.target.checked }))}
                                        className="w-3 h-3 accent-blue-500"
                                    />
                                    <span className={globalSettings?.walkthroughMode ? "text-blue-400 font-bold" : ""}>Walkthrough Mode</span>
                                </label>
                                <span className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity cursor-help" title="Dim all players and hide routes until a player is clicked.">⚙️</span>
                            </div>
                            {globalSettings?.walkthroughMode && (
                                <div className="pl-5 mb-2">
                                    <div className="flex justify-between text-[8px] text-gray-500 mb-1 uppercase">
                                        <span>Background Opacity</span>
                                        <span>{Math.round((globalSettings.walkthroughOpacity || 0.2) * 100)}%</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0.05" max="0.6" step="0.05"
                                        value={globalSettings.walkthroughOpacity || 0.2}
                                        onChange={(e) => setGlobalSettings(s => ({ ...s, walkthroughOpacity: parseFloat(e.target.value) }))}
                                        className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                    />
                                </div>
                            )}
                        </div>
                        <label className="flex items-center gap-2 text-[10px] text-gray-400 hover:text-gray-200 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={globalSettings?.showRoutes !== false}
                                onChange={(e) => setGlobalSettings(s => ({ ...s, showRoutes: e.target.checked }))}
                                className="w-3 h-3 accent-blue-500"
                            />
                            <span>Show Routes</span>
                        </label>
                        <label className="flex items-center gap-2 text-[10px] text-gray-400 hover:text-gray-200 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={globalSettings?.showGridlines !== false}
                                onChange={(e) => setGlobalSettings(s => ({ ...s, showGridlines: e.target.checked }))}
                                className="w-3 h-3 accent-blue-500"
                            />
                            <span>Show Gridlines & Numbers</span>
                        </label>
                        <label className="flex items-center gap-2 text-[10px] text-gray-400 hover:text-gray-200 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!globalSettings?.outerCircleMatchColor}
                                onChange={(e) => setGlobalSettings(s => ({ ...s, outerCircleMatchColor: e.target.checked }))}
                                className="w-3 h-3 accent-blue-500"
                            />
                            <span className={globalSettings?.outerCircleMatchColor ? "text-blue-400 font-bold" : ""}>Outer Circle Match Player Color</span>
                        </label>
                        <label className="flex items-center gap-2 text-[10px] text-gray-400 hover:text-gray-200 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!globalSettings?.printMode}
                                onChange={(e) => setGlobalSettings(s => ({ ...s, printMode: e.target.checked }))}
                                className="w-3 h-3 accent-blue-500"
                            />
                            <span className={globalSettings?.printMode ? "text-blue-400 font-bold" : ""}>Print Mode</span>
                        </label>
                    </div>

                    {/* Icon Size */}
                    <div className="mt-1">
                        <div className="flex justify-between text-[8px] text-gray-500 mb-1 uppercase">
                            <span>Icon Size</span>
                            <span>{globalSettings?.iconSize?.toFixed(1)}x</span>
                        </div>
                        <input
                            type="range" min="0.5" max="2.0" step="0.1"
                            value={globalSettings?.iconSize || 1.0}
                            onChange={(e) => setGlobalSettings(s => ({ ...s, iconSize: parseFloat(e.target.value) }))}
                            className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                </div>
            </div>

        </div>
    );
}
